import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useConfirmationContext } from "@contexts/ConfirmationContext";
import Group from "@ltypes/group";
import Product from "@ltypes/product";
import ProductService from "@services/ProductService";
import GroupList from "@components/organisms/GroupList/GroupList";
import GroupForm from "@components/organisms/GroupForm/GroupForm";
import Loader from "@components/ui/Loader/Loader";
import Button from "@components/forms/Button/Button";
import Alert from "@components/ui/Alert/Alert";
import { MdWifiOff } from "react-icons/md";

type GroupFormData = {
    name: string,
    color: number,
    allDay: boolean,
    startTime: string,
    stopTime: string,
    repeat: Array<number>,
};

type GroupsPageParams = {
    productId: string,
};

function GroupsPage() {
    const params = useParams<GroupsPageParams>();
    const groupFormRef = useRef<HTMLDivElement>(null);

    const [isLoading, setLoading] = useState<boolean>(true);
    const [isFormOpened, setFormOpened] = useState<boolean>(false);
    const [lumeba, setLumeba] = useState<Product>();
    const [lastGroupId, setLastGroupId] = useState<string>();
    const [groupToUpdate, setGroupToUpdate] = useState<Group|undefined>();
    const [isConnected, setConnected] = useState<boolean>(true);

    const { t } = useTranslation();

    const service = new ProductService;

    const { openConfirmationModal } = useConfirmationContext();

    const createGroup = (formData: GroupFormData): Promise<any> => {
        return new Promise<any>((resolve, reject) => {
            if(!lumeba)
                return reject();
            const promise = service.createGroup(lumeba.id, formData.name, formData.color, formData.allDay, formData.startTime, formData.stopTime, formData.repeat);
            toast.promise(promise, {
                loading: t('toast.creating'),
                success: data => t('toast.created'),
                error: err => err.body && err.body.message || t('toast.error_fetching'),
            });
            
            promise.then((data: any) => {
                const newGroup: Group = data.body;
                setLastGroupId(newGroup.id);

                setLumeba({
                    ...lumeba,
                    groups: [
                        ...lumeba.groups,
                        newGroup,
                    ],
                });

                return resolve(true);
            }).catch(() => {
                return reject();
            });
        });
    };

    const updateGroup = (formData: GroupFormData): Promise<any> => {
        return new Promise<any>((resolve, reject) => {
            if(!lumeba || !groupToUpdate)
                return reject();
            const updatedGroup: Group = {
                ...groupToUpdate,
                name: formData.name,
                color: formData.color,
                all_day: formData.allDay,
                start_time: formData.startTime,
                stop_time: formData.stopTime,
                repeat: formData.repeat,
            };
            const promise = service.updateGroup(lumeba.id, updatedGroup);
            toast.promise(promise, {
                loading: t('toast.updating'),
                success: data => t('toast.updated'),
                error: err => err.body && err.body.message || t('toast.error_fetching'),
            });

            const updatedGroupsList: Array<Group> = lumeba.groups.map((g: Group) => {
                return g.id === updatedGroup.id ? updatedGroup : g;
            });
            
            promise.then(() => {
                setLumeba({
                    ...lumeba,
                    groups: updatedGroupsList,
                });

                return resolve(true);
            }).catch(() => {
                return reject();
            });
        });
    };

    const handleSubmitGroup = (name: string, color: number, allDay: boolean, startTime: string, stopTime: string, repeat: Array<number>): Promise<any> => {
        const formData: GroupFormData = {
            name, color, allDay, startTime, stopTime, repeat
        };

        if(groupToUpdate) {
            return updateGroup(formData);
        }
        else {
            return createGroup(formData);
        }
    };

    const handleDeleteGroup = (group: Group) => {
        if(!lumeba)
            return;
        
        openConfirmationModal(t('page.groups.confirmation_delete'), () => {
            const promise = service.deleteGroup(lumeba.id, group.id);
            toast.promise(promise, {
                loading: t('toast.deleting'),
                success: data => t('toast.deleted'),
                error: err => err.body && err.body.message || t('toast.error_fetching'),
            });
            
            promise.then(() => {
                setLumeba({
                    ...lumeba,
                    groups: lumeba.groups.filter(g => g.id !== group.id),
                });
            }).catch(() => {
            });
        });
    };

    const handleUpdateGroup = (group: Group) => {
        setGroupToUpdate(group);
        setFormOpened(true);
    };

    const handleFormSubmit = () => {
        setFormOpened(true);
    };

    const handleFormCancel = () => {
        setGroupToUpdate(undefined);
        setFormOpened(false);
    };

    useEffect(() => {
        if(!params.productId)
            return;

        let connected: boolean|null = null;

        service.get(params.productId).then((data: any) => {
            setLoading(false);
            connected = data.body.is_connected;
            setLumeba(data.body);
        }).finally(() => {
            setLoading(false);
            if(connected === false) {
                setConnected(false);
            }
        });
    }, [params.productId]);

    useEffect(() => {
        if(!isFormOpened)
            return;
        groupFormRef?.current?.scrollIntoView({ behavior: 'smooth' });
    }, [isFormOpened]);

    return (
        <>
            <div className="p-3" style={{marginBottom: '4rem'}}>
                <h1>{t('title.groups')}</h1>

                {isLoading ? (
                    <Loader size="5rem"/>
                ) : !lumeba ? (<></>) : (
                    <>
                        {!isConnected && (
                            <div className="row mt-3">
                                <div className="col-12">
                                    <Alert color="yellow">
                                        <div className="row align-items-center">
                                            <div className="col-auto">
                                                <MdWifiOff size="3rem"/>
                                            </div>
                                            <div className="col">
                                                {t('page.dashboard.offline')}
                                            </div>
                                        </div>
                                    </Alert>
                                </div>
                            </div>
                        )}

                        {lumeba.groups.length > 0 && (
                            <div className="mt-3">
                                <div className="row g-2">
                                    <GroupList product={lumeba} newId={lastGroupId} onDelete={handleDeleteGroup} onUpdate={handleUpdateGroup}/>
                                </div>
                            </div>
                        )}

                        <div className="row mt-3">
                            <div className="col-12">
                                <div className="row">
                                    {!isFormOpened ? (
                                        <>
                                            <div className="col-12 text-center d-none d-md-block">
                                                <Button onClick={handleFormSubmit}>{t('page.groups.input.create_new_group')}</Button>
                                            </div>
                                            <div className="col-12 text-center d-block d-md-none">
                                                <Button onClick={handleFormSubmit} filled>{t('page.groups.input.create_new_group')}</Button>
                                            </div>
                                        </>
                                    ) : (
                                        <div ref={groupFormRef} className="col-12">
                                            <GroupForm group={groupToUpdate} onSubmit={handleSubmitGroup} onCancel={handleFormCancel}/>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </>
    );
}

export default GroupsPage;
